import React, { useEffect, useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useSelector, useDispatch } from "react-redux";
import axios from "axios";

import AdminSideBar from "../../common/AdminSideBar/AdminSideBar";
import { getSupportRequests } from "../../redux/supportSlice";

const API_URL = process.env.REACT_APP_API_URL;

const AdminSupportsPage = () => {
  const dispatch = useDispatch();
  const { supportRequests = [], loading = false, error = null } = useSelector((state) => state.support);

  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [replySending, setReplySending] = useState(false);
  const [repliedIds, setRepliedIds] = useState(new Set());

  useEffect(() => {
    dispatch(getSupportRequests());
  }, [dispatch]);

  useEffect(() => {
    if (error) {
      toast.error(error.message || "Failed to load support requests", {
        position: "top-right",
        autoClose: 5000,
      });
    }
  }, [error]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleReplyOpen = (requestId) => {
    setReplyingTo(requestId);
    setReplyText("");
  };

  const handleReplyCancel = () => {
    setReplyingTo(null);
    setReplyText("");
  };

  const handleReplySend = async (request) => {
    if (!replyText.trim()) return;
    setReplySending(true);
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API_URL}/support/${request._id || request.id}/reply`,
        { replyMessage: replyText },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRepliedIds((prev) => new Set(prev).add(request._id || request.id));
      setReplyingTo(null);
      setReplyText("");
      toast.success(`Reply sent to ${request.email}`);
    } catch (err) {
      toast.error("Failed to send reply. Please try again.");
    } finally {
      setReplySending(false);
    }
  };

  if (loading && supportRequests.length === 0) {
    return (
      <div className="flex flex-col md:flex-row min-h-screen bg-gray-50">
        <div className="md:shrink-0 md:mr-6">
          <AdminSideBar />
        </div>
        <div className="flex-1 p-4 md:p-6">
          <div className="max-w-7xl mx-auto w-full">
            <div className="flex justify-center items-center min-h-[400px]">
              <div className="text-center">
                <div className="w-16 h-16 border-4 border-blue-200 border-t-blue-500 rounded-full animate-spin mx-auto mb-4"></div>
                <h6 className="text-lg font-medium text-gray-700">
                  Loading support requests...
                </h6>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-col md:flex-row min-h-screen bg-gray-50">
        <div className="md:shrink-0 md:mr-6">
          <AdminSideBar />
        </div>
        <div className="flex-1 p-4 md:p-6">
          <div className="max-w-7xl mx-auto w-full">
            <div className="flex items-center justify-between mb-6">
              <h1 className="text-3xl font-semibold text-blue-600">
                Support Requests Management
              </h1>
              <div className="text-sm text-gray-500">
                Total: {supportRequests.length} requests
              </div>
            </div>

            {supportRequests.length === 0 ? (
              <div className="bg-white rounded-lg shadow-sm border p-8 text-center">
                <h3 className="text-lg font-medium text-gray-700 mb-2">
                  No support requests yet
                </h3>
                <p className="text-gray-500">
                  Support requests will appear here when users submit them
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {supportRequests.map((request) => {
                  const id = request._id || request.id;
                  const isReplying = replyingTo === id;
                  const hasReplied = repliedIds.has(id);

                  return (
                    <div
                      key={id}
                      className="bg-white rounded-lg shadow-sm border hover:shadow-md transition-all duration-200"
                    >
                      <div className="p-6">
                        {/* Topic */}
                        <div className="flex items-center gap-3 mb-3">
                          <div className="text-blue-500">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                            </svg>
                          </div>
                          <h3 className="text-lg font-semibold text-blue-600">
                            {request.topic}
                          </h3>
                          {hasReplied && (
                            <span className="ml-auto text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">
                              Replied
                            </span>
                          )}
                        </div>

                        {/* Meta */}
                        <div className="flex flex-wrap items-center gap-6 mb-4 text-sm">
                          <div className="flex items-center gap-2">
                            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                            <span className="font-medium text-gray-700">{request.fullName}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                            </svg>
                            <span className="text-gray-600">{request.email}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span className="text-gray-500">{formatDate(request.createdAt)}</span>
                          </div>
                        </div>

                        {/* Description */}
                        <p className="text-gray-700 leading-relaxed mb-4">
                          {request.description}
                        </p>

                        {/* Reply area */}
                        {isReplying ? (
                          <div className="border-t border-gray-100 pt-4">
                            <p className="text-sm text-gray-500 mb-2">
                              Replying to <span className="font-medium text-gray-700">{request.email}</span>
                            </p>
                            <textarea
                              className="w-full border border-gray-300 rounded-lg p-3 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-300 resize-none"
                              rows={4}
                              placeholder="Write your reply..."
                              value={replyText}
                              onChange={(e) => setReplyText(e.target.value)}
                              autoFocus
                            />
                            <div className="flex justify-end gap-2 mt-2">
                              <button
                                onClick={handleReplyCancel}
                                className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                              >
                                Cancel
                              </button>
                              <button
                                onClick={() => handleReplySend(request)}
                                disabled={replySending || !replyText.trim()}
                                className="px-4 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                              >
                                {replySending && (
                                  <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                )}
                                Send Reply
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="border-t border-gray-100 pt-4 flex justify-end">
                            <button
                              onClick={() => handleReplyOpen(id)}
                              className="flex items-center gap-2 px-4 py-2 text-sm text-blue-600 border border-blue-300 rounded-lg hover:bg-blue-50 transition-colors"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                              </svg>
                              {hasReplied ? "Reply Again" : "Reply"}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </>
  );
};

export default AdminSupportsPage;
